import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import { Button, Grid, TextField } from '@material-ui/core';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import commonFunctions from '../../utils/commonFunctions';
import _uniqueId from 'lodash/uniqueId';
import _filter from 'lodash/filter';
import _each from 'lodash/each';
import { useYearlyContext } from './YearlyContext';

const useStyles = makeStyles((theme) => ({
    addButton: {
        marginTop: theme.spacing(1),
    },
}));

function YearlyWrite ({ onYearlyVacAdd, isUpdating, vacationNumber, daysToBid, freezeBidding, slotsAvailable }) {
    const [id, setId] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [inclusiveEndDate, setInclusiveEndDate] = useState(null);
    const [numOfVacDays, setNumOfVacDays] = useState('');
     
    const [startDateError, setStartDateError] = useState(false);
    const [endDateError, setEndDateError] = useState(false);
    const [startDateMessage, setStartDateMessage] = useState('');
    const [endDateMessage, setEndDateMessage] = useState('');
    const [addButtonDisabled, setAddButtonDisabled] = useState(true);
    const { biddingClosed} = useYearlyContext();

    const firstRender = useRef(true);
    const biddingYear = new Date().getFullYear() + 1;
    const minDate = new Date(biddingYear,0,1);
    const maxDate = new Date(biddingYear,11,31);
    const classes = useStyles(); 

    const handleYearlyVacAdd = () => {
        const newItem = {
            id,
            startDate,
            inclusiveEndDate,
            numOfVacDays,
            vacationNumber,
        };
        onYearlyVacAdd(newItem);
    };

    useEffect(() => {
        if (isUpdating) {
            setId(_uniqueId('id_'));
            setStartDate(null);
            setInclusiveEndDate(null);
            setNumOfVacDays('');
        }
    }, [isUpdating]);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return; // skip the code below
        }
        let numOfDays = 0;
        
        const validate = () => {
            // this is the date validation, returns TRUE if all is good
            setStartDateError(false);
            setEndDateError(false);
            setStartDateMessage('');
            setEndDateMessage('');
            let result = true;

            if (startDate === null || inclusiveEndDate === null){
                result = false;
            } else {
                if (Date.parse(startDate) > Date.parse(inclusiveEndDate)) {
                    setStartDateError(true);
                    setStartDateMessage('Start Date must be on or before End Date');
                    result = false;
                }
            }
            if (result) {  // if the result is still true (so we don't overwrite error messages)
                numOfDays = commonFunctions.calculateNumberOfDays(startDate, inclusiveEndDate);
                // numOfDays cannot be > daysToBid
                if (numOfDays > daysToBid){
                    setStartDateError(true);
                    setStartDateMessage('Bid cannot be > than allowed days to bid');
                    result = false;
                }
            }
            if (result) {
                // if they have more than 4 days to bid, their bids must be at least 4 days long
                // (if they have 4 or less days to bid, they may make < 4 day bids)
                if (daysToBid > 4 && numOfDays < 4){
                    setStartDateError(true);
                    setStartDateMessage('Bid must be at least 4 days');
                    result = false;
                }
            }
            // bid must fall within one of the bid periods
            if (result) {
                if (!commonFunctions.isWithinBidPeriod(startDate, inclusiveEndDate, biddingYear)) {
                    setStartDateError(true);
                    setStartDateMessage('Bid cannot cross bid periods');
                    result = false;
                }
            }

            //the bid must have all days available
            if (result) {
                let unavSlots = [];
                //get the slots available for this date range
                var slots = _filter(slotsAvailable, function(o) {
                    return moment(o.slotDate).format() >= moment(startDate).format() && moment(o.slotDate).format() <= moment(inclusiveEndDate).format();
                });
                //if any of them have 0 slots available, flag it
                _each(slots, function(o) {
                    if (o.availSlotsForDay < 1) {
                        let date = new Date(o.slotDate);
                        let unavDate = (date.getMonth() + 1) + '/' + date.getDate();
                        unavSlots.push(unavDate);
                        setStartDateError(true);
                        result = false;
                    }
                });
                
                if(unavSlots.length > 0){
                    setStartDateMessage('No slots available on ' + unavSlots.join(', '));
                }
            }

            return result;
        };
        
        var valid = validate();
        //the add button is disabled if validation is not passed
        setAddButtonDisabled(!valid);
        
        if (valid) { setNumOfVacDays(numOfDays); }
        else { setNumOfVacDays(''); }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, inclusiveEndDate]);

    return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                    <DatePicker
                        name="startDate"
                        label="Start Date"
                        value={startDate}
                        format="MM/DD/YYYY"
                        inputVariant="outlined"
                        margin="dense"
                        autoOk
                        clearable
                        fullWidth
                        onChange={(e) => (e == null ? setStartDate(null) : setStartDate(moment(e).format('MM/DD/YYYY')))}
                        error={startDateError}
                        helperText={startDateMessage}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <DatePicker
                        name="inclusiveEndDate"
                        label="Inclusive End Date"
                        value={inclusiveEndDate}
                        initialFocusedDate={startDate}
                        format="MM/DD/YYYY"
                        inputVariant="outlined"
                        margin="dense"
                        autoOk
                        clearable
                        fullWidth
                        onChange={(e) => (e == null ? setInclusiveEndDate(null) : setInclusiveEndDate(moment(e).format('MM/DD/YYYY')))}
                        error={endDateError}
                        helperText={endDateMessage}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField
                        name="desc"
                        label="Number of Days"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                        value={numOfVacDays}
                        disabled
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <Button
                        variant="contained"
                        color="primary"
                        margin="dense"
                        onClick={() => { handleYearlyVacAdd(); }}
                        disabled={addButtonDisabled || freezeBidding || biddingClosed}
                        className={classes.addButton}

                    >
                        Add
                    </Button>
                </Grid>
            </Grid>
        </MuiPickersUtilsProvider>
    );
};

YearlyWrite.propTypes = {
    onYearlyVacAdd: PropTypes.func.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    vacationNumber: PropTypes.number.isRequired,
};

export default YearlyWrite;
